import React, { useState, useEffect, useMemo } from 'react';
import { Collapse } from 'antd';
import Button from 'react-bootstrap/Button';

import { useInterval, useRouter, useExcludePlayers } from '../../../helpers/customHooks';
import PeriodsTable from '../../../components/Viewer/TabManage/PeriodsTable';
import PracticeHeader from '../../../components/Viewer/TabManage/PracticeHeader';
import { LIVE_PRACTICE, PAST_PRACTICE, REFRESH_INTERVALS, DEFAULT_PERIODS_VALIDATION_STATE } from '../../../config/constants';
import Loader from '../../Shared/Loader';
import PlayLogger from '../../../components/Viewer/TabManage/PlayLogger';
import ExcludePlayersModal from './ExcludePlayersModal/ExcludePlayersModal';
import { isTMSavable } from '../../../helpers/helpers';
import {
  useDataEventsState,
  usePracticeDataUpdateDispatch,
  usePlayersDispatch,
  usePlayersState,
  usePlayerParticipationState,
  usePlayerParticipationDispatch,
} from "store/hooks";
import { useManageDispatch } from 'store/hooks/manage';

import './TabManage.scss';
import { Drill, Manage, customEventsType } from 'config/commonTypes';
import { updatePracticeData} from 'store/actions/practiceDataUpdate';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducers';
import { useCustomEventsDispatch, useCustomEventsState } from 'store/hooks/customEvents';

const { Panel } = Collapse;
const TabManage = () => {
  const [open, setOpen] = useState(false);
  const { dataEvent:data, data:manageData, meta, mongoId } = useDataEventsState();
  const{ customEvents } = useCustomEventsState();
  const { playerParticipationData } = usePlayerParticipationState();
  const { excludedPlayersByPeriod } = usePlayersState();
  const { loadPlayerParticipation } = usePlayerParticipationDispatch();
  const { loadPlayersExcludedByPeriod, savePlayersExcludedByPeriod } = usePlayersDispatch();
  const teamId = meta.homeTeam.teamId;
  const practice = manageData.manage;
  const eventId = meta._id || undefined;
  const league = meta.league || undefined;
  const isMobileLand = window.matchMedia("only screen and (max-width: 900px)").matches;
  const isMobile = window.matchMedia("only screen and (max-width: 575.98px)").matches;
  const {pathname:path} = useRouter();
  const { getPractice } = useManageDispatch();
  const {  loadCustomEvents } = useCustomEventsDispatch();
  const { updatePracticeData, resetEditbale } = usePracticeDataUpdateDispatch();
  const newPractice = (practice:Manage) => {
    const newPractice = JSON.parse(JSON.stringify(practice));
    if (newPractice.drills) {
      for (let i = 0; i < newPractice.drills.length; ++i) {
        newPractice.drills[i]['drillId'] = i;
      }
    }
    return newPractice;
  };
  const shouldSave = useSelector((state: RootState) => state.practiceDataUpdate.noSave);
  const [currentPractice, setCurrentPractice] = useState<Manage>(newPractice(practice));
  useEffect(() => {
    setCurrentPractice(newPractice(practice));
  }, [practice]);

  useEffect(() => {
    getPractice(eventId, league!, isMobile);
  }, [path]);

  useEffect(() =>{
    updatePracticeData(currentPractice);
    resetEditbale(editable);

  },[currentPractice]);
  useEffect(() => {
    loadCustomEvents(teamId, isMobile);
  }, []);

  const [editable, setEditable] = useState(false);
  // this stateful array holds the messages which will be displayed by
  // each of the End Time and Start Time tooltips on the table
  // it starts as an object for each case with keys and messages are set as errors are verified
  const [periodsValidation, setPeriodsValidation] = useState(DEFAULT_PERIODS_VALIDATION_STATE);

  useEffect(() => {
    if (!editable && !isTMSavable(periodsValidation)) {
      setPeriodsValidation(DEFAULT_PERIODS_VALIDATION_STATE);
    }
  }, [editable]);

  useInterval(() => {
    if (!editable) {
      getPractice(eventId, league!, isMobile);
      loadCustomEvents(teamId, isMobile);
    }
  }, REFRESH_INTERVALS.MANAGE);

  useEffect(() => {
    loadPlayerParticipation(meta.gameId, false);
  }, [meta.gameId]);

  useEffect(() => {
    loadPlayersExcludedByPeriod(meta.gameId);
  }, [meta.gameId]);

  const customEventsButtons:customEventsType = customEvents&& customEvents[teamId];
  const [isInProgressPeriod, setIsInProgressPeriod] = useState(false);
  const [updateQueue, setUpdateQueue] = useState({ queue: [], fetching: 0 });

  const areBlankNamedPeriods = useMemo(() => {
    let isSavable = false;
    currentPractice && currentPractice.drills && currentPractice.drills.forEach((drill:Drill) => {
      if (drill.drillType === '') {
        isSavable = true;
      }
    });
    return isSavable;
  }, [currentPractice]);

  const periodsAreInTime = useMemo(() => {
    const now = (new Date()).getTime();
    let hasFutureDrill = false;
    let isSavable = false;
    currentPractice && currentPractice.drills && currentPractice.drills.forEach(drill => {
      if (drill.startTs > now || drill.endTs > now) {
        hasFutureDrill = true;
      }
    });
    if (hasFutureDrill) {
      isSavable = true;
      return isSavable;
    }
  }, [currentPractice]);

  const savable = isTMSavable(periodsValidation) && !areBlankNamedPeriods && !periodsAreInTime;

  const {
    currentPeriodId,
    currentPeriodName,
    showExcludePlayersModal,
    onOpenExcludePlayersModal,
    onCloseExcludePlayersModal,
    onApplyExcludePlayersModal,
    excludedCountByPeriod = [],
  } = useExcludePlayers(currentPractice, meta.gameId, excludedPlayersByPeriod, savePlayersExcludedByPeriod);

  const playersCount: number = (
    playerParticipationData[meta.gameId] || []
  ).length;
  const participantsByPeriod = useMemo(() => {
    const drills = currentPractice?.drills ?? [];

    return drills.map((_, index) => {
      const excludedCount = excludedCountByPeriod[index] ?? 0;
      const included = Math.max(playersCount - excludedCount, 0);

      return {
        included,
        total: playersCount,
        all: playersCount > 0 && excludedCount === 0,
      };
    });
  }, [currentPractice?.drills, excludedCountByPeriod, playersCount]);


  return (
    <>
      {showExcludePlayersModal && (
        <ExcludePlayersModal
          show={showExcludePlayersModal}
          onClose={onCloseExcludePlayersModal}
          onApply={onApplyExcludePlayersModal}
          players={playerParticipationData[meta.gameId] ?? []}
          periodId={currentPeriodId}
          periodName={currentPeriodName}
        />
      )}
      <Loader style={{ height: 'calc(100vh - 95px)' }}>
        <div className="new-manage">
          <PracticeHeader
            shouldSave={shouldSave}
            editable={editable}
            setEditable={setEditable}
            currentPractice={currentPractice}
            setCurrentPractice={setCurrentPractice}
            savable={savable}
            newPractice={newPractice}
            updateQueue={updateQueue}
          />
        </div>
        <div className="manage-head">
          Practice Periods
        </div>
        <div className="manage-body monitor" style={{ overflow: 'hidden' }}>
          <div
            style={{width:"1277px", height: isMobile? editable? '92%' : '100%' : isMobileLand? editable? '82%' : '100%' :
             (data.meta.type === LIVE_PRACTICE || data.meta.type === PAST_PRACTICE) ? (open ? 'calc(100vh - 552px)' : 'calc(100vh - 264px)') : '100%' }}
            className="periods-table-box periods-table-width"
          >
            <PeriodsTable
              shouldSave={shouldSave}
              editable={editable}
              updateQueue={updateQueue}
              currentPractice={currentPractice}
              periodsValidation={periodsValidation}
              setCurrentPractice={setCurrentPractice}
              setPeriodsValidation={setPeriodsValidation}
              setIsInProgressPeriod={setIsInProgressPeriod}
              onOpenExcludePlayersModal={onOpenExcludePlayersModal}
              participantsByPeriod={participantsByPeriod}
            />
          </div>
          {(data.meta.type === LIVE_PRACTICE || data.meta.type === PAST_PRACTICE) && <div className="play-logger-box">
            <PlayLogger
            //@ts-ignore
              open={open}
              setOpen={setOpen}
              isInProgressPeriod={isInProgressPeriod}
              updateQueue={updateQueue}
              //@ts-ignore
              setUpdateQueue={setUpdateQueue}
              customEventsButtons={customEventsButtons}
            />
          </div>}
        </div>
      </Loader>
    </>
  );
};
export default TabManage;
